import { inject, Injectable } from '@angular/core';
import { Observable, of, take, tap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiRequests } from './api.config';
import {
  IFolder,
  IFoldersResponse,
  IFooterResponse,
  IHome_FranchisesResponse,
  IHomeResponse,
  INews_Items,
  ISelectedCommunications,
  TPageDataWithHome,
} from '@shared/constants/models.const';
import { Router } from '@angular/router';
import { HelperService } from './helper.service';
import { FranchiseService } from './franchise.service';
import { ICollectionApiResponse } from '@shared/components/collections/collection.model';
import { IMySettings, IMySettingsRequest } from '../pages/manage-my-topics/manage-my-settings.model';
import {
  IGetTopicsContentTypesResponse,
  IGetTopicsItemsResponse,
  TypesAPIMapObj,
} from '../pages/my-topics/my-topics.model';

@Injectable({
  providedIn: 'root',
})
export class ContentService {

  HELPER = inject(HelperService);

  constructor(
    private http: HttpClient,
    public router: Router,
    private franchiseService: FranchiseService,
  ) {
  }

  getCollectionInfo(collectionId: number, props?: any): Observable<ICollectionApiResponse> {
    const url = ApiRequests.getFolderCollection.replace(':id', `${collectionId}`);
    return this.http.get<ICollectionApiResponse>(url, {
      params: { ...(props || {}) },
    }).pipe(take(1));
  }

  getMainMenu(forceReload = false): Observable<IFoldersResponse[]> {
    if (this.HELPER.menuItems.length && !forceReload) {
      return of(this.HELPER.menuItems);
    } else {
      return this.http.get<IFoldersResponse[]>(ApiRequests.listFolders)
        .pipe(tap(data => {
          if (data.length) {
            this.HELPER.menuItems = data;
          }
        }));
    }
  }

  getFranchises(): Observable<IHome_FranchisesResponse[]> {
    if (this.HELPER.frItems.length) {
      return of(this.HELPER.frItems);
    } else {
      return this.http.get<IHome_FranchisesResponse[]>(ApiRequests.franchise)
        .pipe(
          tap(data => {
            if (data.length) {
              this.HELPER.frItems = data;
              this.franchiseService.setFranchisesList(data);
            }
          }),
        );
    }
  }

  getHomePage(params?: any): Observable<IHomeResponse> {
    return this.http.get<IHomeResponse>(ApiRequests.homePage, {
      params: {
        ...params,
      },
    });
  }


  getFooter(): Observable<IFooterResponse> {
    return this.http.get<IFooterResponse>(ApiRequests.footer);
  }

  getPageByUrl(link: string, param?: any): Observable<TPageDataWithHome> {
    return this.http.get<TPageDataWithHome>(`${ApiRequests.byUrl}/${link}`, {
      params: {
        ...param,
      },
    });
  }

  getRelated(id: string | null, params: any): Observable<any> {
    const url = ApiRequests.related.replace(':id', String(id));
    return this.http.get<any>(url, {
      params: { ...params },
    });
  }

  getTagRelated(id: string | null, params: any): Observable<any> {
    const url = ApiRequests.tagRelated.replace(':id', String(id));
    return this.http.get<any>(url, {
      params: { ...params },
    });
  }

  getAvailableCommunications(): Observable<IFolder> {
    return this.http.get<IFolder>(ApiRequests.getAvailableCommunications);
  }

  getAllCommunications(): Observable<ISelectedCommunications> {
    return this.http.get<ISelectedCommunications>(ApiRequests.getCommunications);
  }

  saveCommunications(communications: number[]): Observable<any> {
    return this.http.post(ApiRequests.saveCommunications, { FolderIds: communications });
  }

  getContinentsParts(parentId: number): Observable<IFoldersResponse[]> {
    const params = new HttpParams().set('parentId', parentId);
    return this.http.get<IFoldersResponse[]>(ApiRequests.listFolders, { params });
  }

  getContinentItems(folderId: number): Observable<INews_Items> {
    // const params = new HttpParams().set('folderId', folderId);
    return this.http.get<INews_Items>(ApiRequests.getContinentItems, {
      params: {
        folderId,
        take: 200,
      },
    });
  }


  getMySettings() {
    return this.http.get<IMySettings>(ApiRequests.getMySettings)
      .pipe(take(1));
  }

  saveMySettings(body: IMySettingsRequest | null = null, frLoc?: string) {
    return this.http.post<any>(`${ApiRequests.saveMySettings}/${frLoc || this.HELPER.frLoc}`, body)
      .pipe(take(1));
  }

  getTopicsContentTypes() {
    return this.http.get<IGetTopicsContentTypesResponse[]>(ApiRequests.getTopicsContentTypes)
      .pipe(take(1));
  }


  getTopicsItems(contentType: string = TypesAPIMapObj.All, skip = 0, takeItems = 10) {
    const apiUrl = ApiRequests.getTopicsItems.replace(':content-type', contentType);
    return this.http.get<IGetTopicsItemsResponse>(apiUrl, {
      params: {
        skip,
        take: takeItems,
      },
    })
      .pipe(take(1));
  }

  markAllTopicsRead(contentType: string = TypesAPIMapObj.All) {
    const apiUrl = ApiRequests.markAllTopicsRead.replace(':content-type', contentType);
    return this.http.post(apiUrl, null).pipe(take(1));
  }

}
